/**
 * Plugins resource hooks. Backs the admin Plugins tab + the public catalog
 * + plugin-detail pages.
 *
 * Wrappers over the Orval-generated `pluginsList` / `pluginsGetByName` /
 * `pluginsUpdate` / `pluginsDeprecate` / `pluginsUnmarkDeprecated` /
 * `pluginsYank` / `pluginsUnyank` fetchers. The list wrapper adapts
 * `ListQueryParams` for `useEntityData`; the mutation wrappers own React
 * Query cache invalidation for the `['plugins']` and `['plugin-by-name']`
 * prefixes (a lifecycle change must refresh an open detail page, not just
 * the list).
 *
 * **Never resolve a slug by listing.** `/catalog/:slug` is served by
 * {@link usePluginByName} against the slug-addressed route. The previous
 * shape — fetch a page of `useListPlugins()` and `.find()` the slug in JS —
 * 404'd every plugin outside the default 25-row page. See ADR 0009 and the
 * capped-page rule in `marketplace-ui/CLAUDE.md`.
 */
import {
  pluginsDeprecate,
  pluginsGetByName,
  pluginsList,
  pluginsUnmarkDeprecated,
  pluginsUnyank,
  pluginsUpdate,
  pluginsYank,
} from '@/api/generated';
import { type ListQueryParams, flattenListParams } from '@/api/hooks/_helpers';
import type { PluginsListParams } from '@/api/schemas/pluginsListParams';
import type { PaginatedResponse } from '@cotality/marketplace-contracts/endpoints';
import type {
  DeprecatePluginResponse,
  GetPluginByNameResponse,
  UnmarkDeprecatedPluginResponse,
  UnyankPluginResponse,
  UpdatePluginBody,
  UpdatePluginResponse,
  YankPluginResponse,
} from '@cotality/marketplace-contracts/endpoints/plugins';
import type { PluginListItem } from '@cotality/marketplace-contracts/schemas/plugin';
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';

export function useListPlugins(
  params?: ListQueryParams,
  options?: { enabled?: boolean },
): UseQueryResult<PaginatedResponse<PluginListItem>> {
  const flat = flattenListParams(params) as PluginsListParams | undefined;
  return useQuery<PaginatedResponse<PluginListItem>>({
    queryKey: ['plugins', params ?? {}],
    queryFn: ({ signal }) =>
      pluginsList(flat, signal) as Promise<PaginatedResponse<PluginListItem>>,
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
  });
}

/**
 * Slug-addressed plugin fetch (`GET /api/plugins/by-name/:name`) — the
 * canonical resolver for `/catalog/:slug`. Returns the contract's
 * `pluginDetailSchema` payload — the list row plus `readmeContent`,
 * `skills`, and `sourceUrl`.
 *
 * There is no id-addressed counterpart hook: every UI surface addresses a
 * plugin by slug, so `usePluginDetail` (over `GET /api/plugins/:id`) was
 * deleted once the slug-scan resolver went away. The `:id` route itself
 * remains a legitimate API surface — the CLI emits it and every plugin
 * mutation is id-addressed.
 *
 * Both the detail page and its breadcrumb consume this hook, so they share
 * one React Query entry and the crumb costs zero extra network. A missing
 * slug surfaces as the query's 404 (`ApiError.status === 404`); callers must
 * not fall back to scanning a list page.
 */
export function usePluginByName(name: string | undefined): UseQueryResult<GetPluginByNameResponse> {
  return useQuery<GetPluginByNameResponse>({
    queryKey: ['plugin-by-name', name ?? ''],
    queryFn: ({ signal }) =>
      pluginsGetByName(name ?? '', signal) as Promise<GetPluginByNameResponse>,
    enabled: typeof name === 'string' && name.length > 0,
  });
}

/**
 * Returns a stable callback that warms the by-name cache for `name` (the
 * SAME key {@link usePluginByName} reads). Call it on card hover/focus so the
 * detail page renders from cache on click. No-op if already cached/in-flight.
 *
 * Keyed by name, not id: the detail page reads `['plugin-by-name', slug]`, so
 * a prefetch keyed by id would warm an entry nothing reads and the hover
 * optimisation would die silently.
 */
export function usePrefetchPluginByName(): (name: string) => void {
  const qc = useQueryClient();
  return useCallback(
    (name: string) => {
      if (name.length === 0) return;
      void qc.prefetchQuery({
        queryKey: ['plugin-by-name', name],
        queryFn: ({ signal }) => pluginsGetByName(name, signal) as Promise<GetPluginByNameResponse>,
      });
    },
    [qc],
  );
}

export function useUpdatePlugin(): UseMutationResult<
  UpdatePluginResponse,
  Error,
  { id: string; body: UpdatePluginBody }
> {
  const qc = useQueryClient();
  return useMutation<UpdatePluginResponse, Error, { id: string; body: UpdatePluginBody }>({
    mutationFn: ({ id, body }) => pluginsUpdate(id, body) as Promise<UpdatePluginResponse>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plugins'] });
      void qc.invalidateQueries({ queryKey: ['plugin-by-name'] });
    },
  });
}

export function useDeprecatePlugin(): UseMutationResult<
  DeprecatePluginResponse,
  Error,
  { id: string }
> {
  const qc = useQueryClient();
  return useMutation<DeprecatePluginResponse, Error, { id: string }>({
    // Generated body type is `{ [k]: unknown }` (no schema body in the
    // contract); send `{}` so the mutator stays JSON-content-type'd.
    mutationFn: ({ id }) => pluginsDeprecate(id, {}) as Promise<DeprecatePluginResponse>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plugins'] });
      void qc.invalidateQueries({ queryKey: ['plugin-by-name'] });
    },
  });
}

export function useYankPlugin(): UseMutationResult<
  YankPluginResponse,
  Error,
  { id: string; reason: string }
> {
  const qc = useQueryClient();
  return useMutation<YankPluginResponse, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => pluginsYank(id, { reason }) as Promise<YankPluginResponse>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plugins'] });
      void qc.invalidateQueries({ queryKey: ['plugin-by-name'] });
    },
  });
}

export function useUnyankPlugin(): UseMutationResult<UnyankPluginResponse, Error, { id: string }> {
  const qc = useQueryClient();
  return useMutation<UnyankPluginResponse, Error, { id: string }>({
    mutationFn: ({ id }) => pluginsUnyank(id, {}) as Promise<UnyankPluginResponse>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plugins'] });
      void qc.invalidateQueries({ queryKey: ['plugin-by-name'] });
    },
  });
}

export function useUnmarkDeprecatedPlugin(): UseMutationResult<
  UnmarkDeprecatedPluginResponse,
  Error,
  { id: string }
> {
  const qc = useQueryClient();
  return useMutation<UnmarkDeprecatedPluginResponse, Error, { id: string }>({
    mutationFn: ({ id }) =>
      pluginsUnmarkDeprecated(id, {}) as Promise<UnmarkDeprecatedPluginResponse>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plugins'] });
      void qc.invalidateQueries({ queryKey: ['plugin-by-name'] });
    },
  });
}
